import { useEffect, useState } from 'react';
import { X, Wand2, Loader2, User, Calendar, Clock, MapPin } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

interface TTEvent {
  timetree_uuid: string;
  title: string;
  note: string;
  location: string;
  start_at: string;
  end_at: string;
  all_day: boolean;
}

interface Client {
  id: string;
  name: string;
  address?: string;
}

const ASSIGNEES = ['sammy', 'jim', 'stewart'];
const JOB_TYPES = [
  'Boiler Service',
  'Boiler Repair',
  'Boiler Install',
  'Blocked Drain',
  'Leak / Burst',
  'Bathroom Install',
  'Kitchen Install',
  'Groundwork',
  'General Plumbing',
  'Other',
];

export default function ConvertTimeTreeModal({
  event,
  onClose,
  onDone,
}: {
  event: TTEvent;
  onClose: () => void;
  onDone: () => void;
}) {
  const [clients, setClients] = useState<Client[]>([]);
  const [mode, setMode] = useState<'existing' | 'new'>('new');
  const [clientId, setClientId] = useState<string>('');
  const [clientQ, setClientQ] = useState('');
  const [clientName, setClientName] = useState(event.title || '');
  const [clientAddress, setClientAddress] = useState(event.location || '');
  const [jobType, setJobType] = useState(JOB_TYPES[0]);
  const [assignee, setAssignee] = useState('jim');
  const [date, setDate] = useState<string>(() => {
    try {
      return format(parseISO(event.start_at), 'yyyy-MM-dd');
    } catch {
      return format(new Date(), 'yyyy-MM-dd');
    }
  });
  const [time, setTime] = useState<string>(() => {
    if (event.all_day) return '09:00';
    try {
      return format(parseISO(event.start_at), 'HH:mm');
    } catch {
      return '09:00';
    }
  });
  const [duration, setDuration] = useState<number>(2);
  const [notes, setNotes] = useState(event.note || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get('/clients')
      .then((r) => setClients(r.data || []))
      .catch(() => setClients([]));
  }, []);

  const filteredClients = clients.filter((c) =>
    c.name.toLowerCase().includes(clientQ.toLowerCase())
  );

  const submit = async () => {
    if (mode === 'existing' && !clientId) {
      toast.error('Pick an existing client, or switch to "New client"');
      return;
    }
    if (mode === 'new' && !clientName.trim()) {
      toast.error('Client name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/timetree/events/${event.timetree_uuid}/convert`, {
        ...(mode === 'existing' ? { client_id: clientId } : { client_name: clientName, client_address: clientAddress }),
        job_type: jobType,
        assigned_to: assignee,
        date,
        time,
        duration_hours: duration,
        notes,
      });
      toast.success(`Job ${res.data.job_number} created`);
      onDone();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not create job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4 animate-fade-in"
      onClick={onClose}
      data-testid="tt-convert-modal"
    >
      <div
        className="card w-full sm:max-w-xl animate-scale-in bg-white shadow-pop rounded-t-2xl sm:rounded-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 grid place-items-center shrink-0">
              <Wand2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-slate-900 leading-tight">
                Convert to Job
              </h3>
              <p className="text-sm text-slate-500 mt-0.5 truncate max-w-[300px]">
                {event.title || '(no title)'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Client picker */}
          <div>
            <label className="label-caps mb-2 block flex items-center gap-1.5">
              <User className="h-3 w-3" /> Client
            </label>
            <div className="flex gap-1 mb-2 text-xs">
              <button
                type="button"
                onClick={() => setMode('new')}
                className={`px-3 py-1.5 rounded-md font-semibold ${mode === 'new' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="tt-convert-mode-new"
              >
                New client
              </button>
              <button
                type="button"
                onClick={() => setMode('existing')}
                className={`px-3 py-1.5 rounded-md font-semibold ${mode === 'existing' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="tt-convert-mode-existing"
              >
                Existing client
              </button>
            </div>
            {mode === 'new' ? (
              <div className="space-y-2">
                <input
                  className="input"
                  placeholder="Client name (e.g. Johanna Mayr)"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  data-testid="tt-convert-client-name"
                />
                <input
                  className="input"
                  placeholder="Address (optional)"
                  value={clientAddress}
                  onChange={(e) => setClientAddress(e.target.value)}
                  data-testid="tt-convert-client-address"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  className="input"
                  placeholder="Search existing clients…"
                  value={clientQ}
                  onChange={(e) => setClientQ(e.target.value)}
                />
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md bg-slate-50">
                  {filteredClients.slice(0, 15).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setClientId(c.id)}
                      className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 hover:bg-white ${clientId === c.id ? 'bg-brand-50' : ''}`}
                    >
                      <div className="font-semibold text-slate-900">{c.name}</div>
                      {c.address && <div className="text-xs text-slate-500 truncate">{c.address}</div>}
                    </button>
                  ))}
                  {filteredClients.length === 0 && (
                    <div className="p-3 text-sm text-slate-400">No matches</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Job type */}
          <div>
            <label className="label-caps mb-1.5 block">Job type</label>
            <select
              className="input"
              value={jobType}
              onChange={(e) => setJobType(e.target.value)}
              data-testid="tt-convert-jobtype"
            >
              {JOB_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Assignee */}
          <div>
            <label className="label-caps mb-1.5 block">Assign to</label>
            <div className="grid grid-cols-3 gap-2">
              {ASSIGNEES.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setAssignee(n)}
                  className={`px-3 py-2.5 rounded-md text-sm font-semibold transition capitalize ${
                    assignee === n ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`tt-convert-assignee-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Date / time / duration */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-caps mb-1.5 block flex items-center gap-1.5">
                <Calendar className="h-3 w-3" /> Date
              </label>
              <input
                type="date"
                className="input"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="tt-convert-date"
              />
            </div>
            <div>
              <label className="label-caps mb-1.5 block flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Time
              </label>
              <input
                type="time"
                className="input"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="tt-convert-time"
              />
            </div>
            <div>
              <label className="label-caps mb-1.5 block">Duration (hrs)</label>
              <input
                type="number"
                className="input"
                step={0.5}
                min={0.5}
                value={duration}
                onChange={(e) => setDuration(parseFloat(e.target.value) || 1)}
                data-testid="tt-convert-duration"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="label-caps mb-1.5 block">Notes</label>
            <textarea
              className="input min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="tt-convert-notes"
            />
          </div>

          {event.location && (
            <div className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" />
              From TimeTree: {event.location}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl sticky bottom-0">
          <button onClick={onClose} className="btn btn-secondary" data-testid="tt-convert-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn btn-primary"
            data-testid="tt-convert-confirm"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Creating…
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" /> Create job
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
